import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Work,
  AccountBalance,
  Description,
  AccessTime,
  Event,
  Edit,
  Save,
  Cancel,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import userService from '../../services/userService';
import shiftService from '../../services/shiftService';
import userDocumentService from '../../services/userDocumentService';
import { User, UserDocument, UserWorkScheduleEntry } from '../../types';
import { USER_ROLES } from '../../constants';
import { UserProfileShimmer } from '../../components/common/Shimmer';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import { useAuth } from '../../hooks/useAuth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Check if current user can edit user profile (only CLM and Super Admin, not Store Manager)
  const canEditUser = currentUser?.role === 'super_admin' || currentUser?.role === 'cluster_level_manager';
  const [formData, setFormData] = useState<Partial<User>>({});
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [workSchedule, setWorkSchedule] = useState<UserWorkScheduleEntry[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [shifts, setShifts] = useState<any[]>([]);
  const DOCUMENT_TYPE_OPTIONS = [
    { value: 'aadhaar', label: 'Aadhaar Card' },
    { value: 'pan', label: 'PAN Card' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'passbook', label: 'Bank Passbook' },
    { value: 'other', label: 'Other' },
  ];

  const DAY_LABELS: { label: string; short: string; value: number }[] = [
    { label: 'Monday', short: 'Mon', value: 1 },
    { label: 'Tuesday', short: 'Tue', value: 2 },
    { label: 'Wednesday', short: 'Wed', value: 3 },
    { label: 'Thursday', short: 'Thu', value: 4 },
    { label: 'Friday', short: 'Fri', value: 5 },
    { label: 'Saturday', short: 'Sat', value: 6 },
    { label: 'Sunday', short: 'Sun', value: 7 },
  ];

  const loadShifts = async () => {
    try {
      const res = await shiftService.getShifts({ limit: 200 });
      setShifts(res.shifts || []);
    } catch (e) {
      // silent; schedule UI still works but with empty shifts
    }
  };

  const loadWorkSchedule = async (userData: User) => {
    try {
      if (!userData.id) return;
      setScheduleLoading(true);
      setScheduleError(null);
      const result = await userService.getUserWorkSchedule(userData.id);
      const map = new Map<number, UserWorkScheduleEntry>();
      result.schedule.forEach((entry) => map.set(entry.day_of_week, entry));

      const full: UserWorkScheduleEntry[] = DAY_LABELS.map((d) => {
        const existing = map.get(d.value);
        return (
          existing || {
            user_id: userData.id,
            day_of_week: d.value,
            is_week_off: d.value === 7, // default Sunday off
            shift_id: userData.shift_id || null,
            shift: null,
          }
        );
      });

      setWorkSchedule(full);
    } catch (e: any) {
      setScheduleError(e?.message || 'Failed to load work timings');
    } finally {
      setScheduleLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const userData = await userService.getUserProfile(userId);
      setUser(userData);
      setFormData(userData); // Initialize form data
      setError(null);

      await Promise.all([loadShifts(), loadWorkSchedule(userData)]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    if (userId) {
      fetchUserDocuments();
    }
  }, [userId]);

  const fetchUserDocuments = async () => {
    if (!userId) return;
    try {
      const docs = await userDocumentService.getByUser(userId);
      setUserDocuments(docs);
    } catch (err) {
      // silent fail; page still loads
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    if (!userId) return;
    
    try {
      setSaving(true);
      
      // Filter out null, undefined, and empty string values
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData).filter(([key, value]) => {
          // Keep the value if it's not null, undefined, or empty string
          return value !== null && value !== undefined && value !== '';
        })
      );
      
      console.log('Original formData:', formData);
      console.log('Cleaned formData:', cleanedFormData);
      
      const updatedUser = await userService.updateUser(userId, cleanedFormData);
      setUser(updatedUser);
      setFormData(updatedUser); // Update form data with the response
      setEditing(false);
      setError(null);
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 8000);
    } catch (err: any) {
      console.error('Update user error:', err);
      
      // Handle validation errors specifically
      if (err.response?.data?.data && Array.isArray(err.response.data.data)) {
        const validationErrors = err.response.data.data;
        const errorMessages = validationErrors.map((error: any) => 
          `${error.field}: ${error.message}`
        ).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else {
        setError(err.response?.data?.message || 'Failed to update user profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    if (user) {
      setFormData(user); // Reset form data to original user data
    }
  };

  const handleFieldChange = (field: keyof User, value: any) => {
    console.log(`Field changed: ${field} = ${value}`);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateScheduleEntry = (index: number, patch: Partial<UserWorkScheduleEntry>) => {
    setWorkSchedule(prev =>
      prev.map((entry, idx) => (idx === index ? { ...entry, ...patch } : entry))
    );
  };

  const handleSaveWorkSchedule = async () => {
    if (!user) return;
    try {
      setScheduleLoading(true);
      const payload = workSchedule.map(entry => ({
        day_of_week: entry.day_of_week,
        is_week_off: entry.is_week_off,
        shift_id: entry.is_week_off ? null : entry.shift_id || null,
      }));
      await userService.updateUserWorkSchedule(user.id, payload);
      setSuccess('Work timings updated successfully!');
      setTimeout(() => setSuccess(null), 8000);
    } catch (e: any) {
      setScheduleError(e?.message || 'Failed to update work timings');
    } finally {
      setScheduleLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      super_admin: 'error',
      cluster_level_manager: 'warning',
      store_manager: 'info',
      quality_manager: 'secondary',
      process_expert: 'secondary',
      shift_in_charge: 'secondary',
      associate: 'default',
      security_staff: 'default',
    };
    return colors[role] || 'default';
  };

  if (loading) {
    return <UserProfileShimmer />;
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button onClick={fetchUserProfile} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

  if (!user) return null;

  return (
    <Box>
      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/users')} color="primary">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600 }}>
            {user.first_name} {user.last_name}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Employee ID: {user.employee_id} • {USER_ROLES[user.role as keyof typeof USER_ROLES]}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEditUser && (
            <>
              {editing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                      },
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={handleEdit}
                  sx={{
                    background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                    },
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Compact Profile Summary */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ProfileAvatar
                  imageUrl={user.profile_image_url}
                  firstName={user.first_name}
                  lastName={user.last_name}
                  sx={{ width: 60, height: 60, fontSize: '1.5rem', bgcolor: '#081122' }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#081122' }}>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    {user.employee_id}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 16, color: '#6c757d' }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>{user.phone}</Typography>
                </Box>
                {user.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email sx={{ fontSize: 16, color: '#6c757d' }} />
                    <Typography variant="body2" sx={{ color: '#333333' }}>{user.email}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {user.store && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 16, color: '#6c757d' }} />
                    <Typography variant="body2" sx={{ color: '#333333' }}>
                      {user.store.name} ({user.store.code})
                    </Typography>
                  </Box>
                )}
                {user.cluster && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 16, color: '#6c757d' }} />
                    <Typography variant="body2" sx={{ color: '#333333' }}>
                      {user.cluster.name} ({user.cluster.code})
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
                <Chip 
                  label={USER_ROLES[user.role as keyof typeof USER_ROLES]} 
                  color={getRoleColor(user.role) as any} 
                  size="small" 
                />
                <Chip 
                  label={user.is_active ? 'Active' : 'Inactive'} 
                  color={user.is_active ? 'success' : 'error'} 
                  size="small" 
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<Person />}
            label="Personal Details"
            iconPosition="start"
          />
          <Tab
            icon={<Work />}
            label="Employment Details"
            iconPosition="start"
          />
          <Tab
            icon={<AccountBalance />}
            label="Bank Details"
            iconPosition="start"
          />
          <Tab
            icon={<Description />}
            label="Documents"
            iconPosition="start"
          />
          <Tab
            icon={<AccessTime />}
            label="Attendance"
            iconPosition="start"
          />
          <Tab
            icon={<Event />}
            label="Leave Management"
            iconPosition="start"
          />
        </Tabs>

        {/* Personal Details Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                Basic Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="First Name"
                  value={formData.first_name || ''}
                  onChange={(e) => handleFieldChange('first_name', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Last Name"
                  value={formData.last_name || ''}
                  onChange={(e) => handleFieldChange('last_name', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Phone Number"
                  value={formData.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Personal Email"
                  value={formData.email || ''}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Date of Birth"
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth disabled={!editing}>
                  <InputLabel>Gender</InputLabel>
                  <Select 
                    value={formData.gender || ''} 
                    label="Gender"
                    onChange={(e) => handleFieldChange('gender', e.target.value)}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                Additional Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth disabled={!editing}>
                  <InputLabel>Marital Status</InputLabel>
                  <Select 
                    value={formData.marital_status || ''} 
                    label="Marital Status"
                    onChange={(e) => handleFieldChange('marital_status', e.target.value)}
                  >
                    <MenuItem value="single">Single</MenuItem>
                    <MenuItem value="married">Married</MenuItem>
                    <MenuItem value="divorced">Divorced</MenuItem>
                    <MenuItem value="widowed">Widowed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled={!editing}>
                  <InputLabel>Blood Group</InputLabel>
                  <Select 
                    value={formData.blood_group || ''} 
                    label="Blood Group"
                    onChange={(e) => handleFieldChange('blood_group', e.target.value)}
                  >
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Guardian Name"
                  value={formData.guardian_name || ''}
                  onChange={(e) => handleFieldChange('guardian_name', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Current Address"
                  value={formData.current_address || ''}
                  onChange={(e) => handleFieldChange('current_address', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Employment Details Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                Job Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Employee ID"
                  value={user.employee_id}
                  disabled
                  fullWidth
                  variant="outlined"
                />
                <FormControl fullWidth disabled={!editing}>
                  <InputLabel>Role</InputLabel>
                  <Select 
                    value={formData.role || user.role} 
                    label="Role"
                    onChange={(e) => handleFieldChange('role', e.target.value)}
                  >
                    {Object.entries(USER_ROLES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled={!editing}>
                  <InputLabel>Employee Type</InputLabel>
                  <Select 
                    value={formData.employee_type || user.employee_type || ''} 
                    label="Employee Type"
                    onChange={(e) => handleFieldChange('employee_type', e.target.value)}
                  >
                    <MenuItem value="permanent">Permanent</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="temporary">Temporary</MenuItem>
                    <MenuItem value="intern">Intern</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Job Title"
                  value={formData.job_title || ''}
                  onChange={(e) => handleFieldChange('job_title', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Official Email"
                  value={formData.official_email || ''}
                  onChange={(e) => handleFieldChange('official_email', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                {/* Write-once. The backend refuses to change an existing
                    joining date, so once it is set the field stays read-only
                    even in edit mode. */}
                <TextField
                  label={user?.joining_date ? 'Date of Joining (locked)' : 'Date of Joining'}
                  type="date"
                  value={formData.joining_date || ''}
                  onChange={(e) => handleFieldChange('joining_date', e.target.value)}
                  disabled={!editing || !!user?.joining_date}
                  helperText={
                    user?.joining_date
                      ? 'Joining date cannot be changed once set'
                      : undefined
                  }
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                Organizational Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Store"
                  value={user.store ? `${user.store.name} (${user.store.code})` : 'N/A'}
                  disabled
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Cluster"
                  value={user.cluster ? `${user.cluster.name} (${user.cluster.code})` : 'N/A'}
                  disabled
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Manager"
                  value={user.manager ? `${user.manager.first_name} ${user.manager.last_name}` : 'N/A'}
                  disabled
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Category Level"
                  value={user.category_level || 'N/A'}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="ESI Number"
                  value={user.esi_number || ''}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="UAN Number"
                  value={user.uan_number || ''}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Bank Details Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                Bank Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Bank Name"
                  value={formData.bank_name || ''}
                  onChange={(e) => handleFieldChange('bank_name', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Account Number"
                  value={formData.bank_account_number || ''}
                  onChange={(e) => handleFieldChange('bank_account_number', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="IFSC Code"
                  value={formData.bank_ifsc || ''}
                  onChange={(e) => handleFieldChange('bank_ifsc', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                Government IDs
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="PAN Number"
                  value={formData.pan_number || ''}
                  onChange={(e) => handleFieldChange('pan_number', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Aadhaar Number"
                  value={formData.aadhaar_number || ''}
                  onChange={(e) => handleFieldChange('aadhaar_number', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Driving License"
                  value={formData.driving_license || ''}
                  onChange={(e) => handleFieldChange('driving_license', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Voter ID"
                  value={formData.voter_id || ''}
                  onChange={(e) => handleFieldChange('voter_id', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" sx={{ mb: 3, color: '#081122', fontWeight: 600 }}>
            User Documents
          </Typography>
          <Card sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="doc-type-label">Document Type</InputLabel>
                  <Select
                    labelId="doc-type-label"
                    label="Document Type"
                    value={(formData as any).document_type || ''}
                    onChange={(e) => {
                      const value = e.target.value as string;
                      handleFieldChange('document_type' as any, value);
                      // suggest a default name from the selected type
                      const opt = DOCUMENT_TYPE_OPTIONS.find(o => o.value === value);
                      handleFieldChange('document_name' as any, opt ? opt.label : 'Document');
                    }}
                  >
                    {DOCUMENT_TYPE_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Document Name"
                  placeholder="Aadhaar Card"
                  value={(formData as any).document_name || ''}
                  onChange={(e) => handleFieldChange('document_name' as any, e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button variant="outlined" component="label" fullWidth>
                  Select Files (max 2)
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const limited = files.slice(0, 2);
                      if (files.length > 2) {
                        setError('You can upload a maximum of 2 files for this document');
                      } else {
                        setError(null);
                      }
                      setSelectedFiles(limited);
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={12}>
                {selectedFiles.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {selectedFiles.map((file, idx) => (
                      <Card key={idx} sx={{ p: 1, px: 2 }}>
                        <Typography variant="body2" sx={{ maxWidth: 260, wordBreak: 'break-all' }}>{file.name}</Typography>
                      </Card>
                    ))}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      const payload: any = {
                        document_type: (formData as any).document_type || 'general',
                        document_name: (formData as any).document_name || 'Unnamed Document'
                      };
                      if (!selectedFiles.length) { setError('Select files to upload'); return; }
                      if (selectedFiles.length > 2) { setError('You can upload a maximum of 2 files'); return; }
                      await userDocumentService.createForUser(userId!, payload, selectedFiles);
                      setSuccess('Documents uploaded successfully');
                      // refresh list - simple re-fetch of user profile or dedicated documents fetch
                      await fetchUserDocuments();
                      setSelectedFiles([]);
                    } catch (err: any) {
                      setError(err.response?.data?.message || 'Upload failed');
                    }
                  }}
                >
                  Upload
                </Button>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Documents</Typography>
            <Box>
              {userDocuments && userDocuments.length > 0 ? (
                <Grid container spacing={2}>
                  {userDocuments.map((doc) => (
                    <Grid key={doc.id} item xs={12} md={6}>
                      <Card sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{doc.document_name} ({doc.document_type})</Typography>
                        <Box sx={{ mt: 1 }}>
                          {(doc.files || []).map((f) => (
                            <Box key={f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                              <Typography variant="body2">{f.original_name}</Typography>
                              <Button size="small" href={f.file_url} target="_blank">View</Button>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">No documents uploaded yet.</Alert>
              )}
            </Box>
          </Card>
        </TabPanel>

        {/* Attendance / Work Timings Tab */}
        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" sx={{ mb: 3, color: '#081122', fontWeight: 600 }}>
            Work Timings
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Configure weekly work timings for this user. Days marked as week off will not be
            counted as absent in attendance.
          </Alert>
          {scheduleError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
              {scheduleError}
            </Alert>
          )}
          {scheduleLoading && workSchedule.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Grid container sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                <Grid item xs={4}>
                  Day
                </Grid>
                <Grid item xs={3}>
                  Week Off
                </Grid>
                <Grid item xs={5}>
                  Shift
                </Grid>
              </Grid>
              {workSchedule.map((entry, index) => {
                const meta = DAY_LABELS.find(d => d.value === entry.day_of_week)!;
                return (
                  <Grid
                    container
                    key={entry.day_of_week}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={4}>
                      <Box>
                        <Typography sx={{ fontWeight: 500 }}>{meta.label}</Typography>
                        {entry.is_week_off && (
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            Every {meta.label} will be a week off
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Checkbox
                        checked={entry.is_week_off}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateScheduleEntry(index, {
                            is_week_off: e.target.checked,
                            shift_id: e.target.checked ? null : entry.shift_id,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <FormControl fullWidth size="small" disabled={entry.is_week_off}>
                        <Select
                          value={entry.shift_id || ''}
                          displayEmpty
                          onChange={e =>
                            updateScheduleEntry(index, {
                              shift_id: (e.target.value as string) || null,
                            })
                          }
                          renderValue={selected => {
                            if (!selected) return 'Select Shift';
                            const shift = shifts.find(s => s.id === selected);
                            return shift ? shift.name : 'Select Shift';
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Shift</em>
                          </MenuItem>
                          {shifts.map(shift => (
                            <MenuItem key={shift.id} value={shift.id}>
                              {shift.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                );
              })}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => user && loadWorkSchedule(user)}
                  disabled={scheduleLoading}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AccessTime />}
                  onClick={handleSaveWorkSchedule}
                  disabled={scheduleLoading}
                >
                  Save Work Timings
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Leave Management Tab */}
        <TabPanel value={activeTab} index={5}>
          <Typography variant="h6" sx={{ mb: 3, color: '#081122', fontWeight: 600 }}>
            Leave Management
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Leave request and management functionality will be implemented here.
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Leave Balance
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Annual Leave" secondary="12 days remaining" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Sick Leave" secondary="5 days remaining" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Personal Leave" secondary="3 days remaining" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Recent Leave Requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No recent leave requests
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default UserProfilePage;
